import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { CompositionService } from "../../../../services/CompositionService";
import { TrainingMethod } from "../../../../model_gen/finetuning";
import { FinetuneData } from "../NewFinetuneRun";
import CompositionOverview from "../../Compositions/CompositionOverview";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Label } from "../../../ui/label";
import { DatabaseIcon, Loader2Icon } from "lucide-react";
import { cn } from "../../../../lib/utils";

interface Composition {
  id: number;
  name: string;
  description: string;
  date_created: number;
  intent_group_name?: string;
  application_name?: string;
  intent_name?: string;
  base_type: string;
  composition_type?: "SFT" | "RL";
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  company_id?: number;
  major_version: number;
  minor_version: number;
  meta: any;
  member_count: number;
  total_request_count: number;
  members?: Array<{
    dataset_id: number;
    dataset_name: string;
    dataset_description: string;
    origin_dataset_id: number;
    origin_dataset_name: string;
    origin_dataset_description: string;
    request_count: number;
    sampling_type: string;
    sampling_weight: number;
  }>;
}

interface ChooseCompositionStepProps {
  trainingMethod: TrainingMethod;
  selectedComposition: string;
  setSelectedComposition: (value: string) => void;
  isCreatingFinetune: boolean;
  setFinetuneData: (updater: (prev: FinetuneData) => FinetuneData) => void;
}

const ChooseCompositionStep: React.FC<ChooseCompositionStepProps> = ({
  trainingMethod,
  selectedComposition,
  setSelectedComposition,
  isCreatingFinetune,
  setFinetuneData,
}) => {
  const { apiKey } = usePermissions();
  const [isLoadingCompositions, setIsLoadingCompositions] = useState<boolean>(false);
  const [compositions, setCompositions] = useState<Composition[]>([]);
  const [selectedCompositionInfo, setSelectedCompositionInfo] = useState<Composition | null>(null);

  const requiredCompType: "SFT" | "RL" =
    trainingMethod === "DPO" ? "RL" : "SFT";

  // Re-fetch when training method changes (server-side filtering)
  useEffect(() => {
    const sub = CompositionService.getCompositions(
      0, 100, setIsLoadingCompositions, apiKey, requiredCompType, true
    ).subscribe(
      (data: any) => {
        let items: Composition[] = [];
        if (data && Array.isArray(data.items)) {
          items = data.items;
        } else if (data && Array.isArray(data)) {
          items = data;
        } else {
          console.error("Unexpected composition response format:", data);
        }
        setCompositions(items);

        // Clear stale selection if the saved composition_id isn't in the new list
        if (selectedComposition && !items.some((c) => c.id.toString() === selectedComposition)) {
          setSelectedComposition("");
          setFinetuneData((prev) => ({
            ...prev,
            composition_id: undefined,
            composition_type: undefined,
            application_id: undefined,
            intent_id: undefined,
            intent_group_id: undefined,
          }));
        }
      },
      (error: Error) => {
        console.error("Error fetching compositions:", error);
      }
    );
    return () => sub.unsubscribe();
  }, [apiKey, requiredCompType]);

  // Fetch full composition detail (with request counts) when selection changes
  useEffect(() => {
    if (!selectedComposition) {
      setSelectedCompositionInfo(null);
      return;
    }
    const sub = CompositionService.getComposition(
      selectedComposition,
      () => {},
      apiKey
    ).subscribe(
      (detail: any) => {
        if (detail) {
          setSelectedCompositionInfo(detail);
        } else {
          const fallback = compositions.find((c) => c.id.toString() === selectedComposition);
          setSelectedCompositionInfo(fallback || null);
        }
      },
      () => {
        const fallback = compositions.find((c) => c.id.toString() === selectedComposition);
        setSelectedCompositionInfo(fallback || null);
      }
    );
    return () => sub.unsubscribe();
  }, [selectedComposition, apiKey]);

  // Handle composition selection
  const handleCompositionChange = (value: string) => {
    setSelectedComposition(value);

    // Find the selected composition to get its IDs
    const selectedComposition = compositions.find((c) => c.id.toString() === value);

    if (selectedComposition) {
      setFinetuneData((prev) => ({
        ...prev,
        composition_id: selectedComposition.id,
        composition_type: selectedComposition.composition_type || "SFT",
        application_id: selectedComposition.application_id,
        intent_id: selectedComposition.intent_id,
        intent_group_id: selectedComposition.intent_group_id,
      }));
    }
  };

  // Helper function to get source label and color
  const getSourceInfo = (composition: Composition) => {
    let sourceLabel = "";
    let sourceColor = "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
    
    if (composition.base_type === "intent_group") {
      sourceLabel = composition.intent_group_name || 
                  (composition.meta && composition.meta.intent_name) ||
                  "Intent Group";
      sourceColor = "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
    } else if (composition.base_type === "application_intent") {
      const appName = composition.application_name || 
                     (composition.meta && composition.meta.application_name) ||
                     "App";
      const intentName = composition.intent_name || 
                        (composition.meta && composition.meta.action_type) ||
                        "Intent";
      sourceLabel = `${appName} / ${intentName}`;
      sourceColor = "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
    }

    return { sourceLabel, sourceColor };
  };

  if (isLoadingCompositions) {
    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <h2 className="text-2xl font-gc-demi-bold text-foreground">
            Choose Composition
          </h2>
          <p className="text-muted-foreground font-gc-reg">
            Showing {requiredCompType} compositions for {trainingMethod} training
          </p>
        </div>

        {/* Loading Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Loader2Icon className="w-8 h-8 animate-spin text-maitai-green mb-4" />
              <div className="space-y-2">
                <h3 className="text-lg font-gc-med text-foreground">
                  Loading compositions...
                </h3>
                <p className="text-sm text-muted-foreground">
                  Please wait while we fetch available compositions
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Choose Composition
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Showing {requiredCompType} compositions for {trainingMethod} training
        </p>
      </div>

      {/* Selection Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <DatabaseIcon className="w-5 h-5 text-maitai-green" />
            Composition Selection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-gc-med text-foreground">
              Composition
            </Label>
            <Select
              value={selectedComposition}
              onValueChange={handleCompositionChange}
              disabled={isCreatingFinetune}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a composition" />
              </SelectTrigger>
              <SelectContent>
                {compositions.map((composition) => {
                  const { sourceLabel, sourceColor } = getSourceInfo(composition);
                  const compType = composition.composition_type || "SFT";
                  
                  return (
                    <SelectItem
                      key={composition.id.toString()}
                      value={composition.id.toString()}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span className="text-sm font-gc-reg">
                          {composition.name}
                        </span>
                        <span className={cn(
                          "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                          compType === "RL"
                            ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                            : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                        )}>
                          {compType}
                        </span>
                        {sourceLabel && (
                          <span className={cn(
                            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
                            sourceColor
                          )}>
                            {sourceLabel}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {compositions.length === 0 && !isLoadingCompositions && (
              <p className="text-sm text-red-600">
                No {requiredCompType} compositions found. Create a composition first.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Selected Composition Overview */}
      {selectedCompositionInfo && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-gc-med text-foreground">
              Composition Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CompositionOverview 
              composition={selectedCompositionInfo} 
              showDistribution={false}
              compact={true}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ChooseCompositionStep; 